/* global Excel */

import { colLetter } from "./sheetBuilder";

/**
 * Builds a "market share" worksheet: the input-driven pattern from
 * sheetBuilder applied to SHEETYBANKS.MARKET. The market code and survey year
 * are plain cells at the top, the column names are a header row, and one
 * spilling MARKET formula fills the table — so retyping the market, the year,
 * or a column name repoints the whole report.
 *
 * Layout:
 *   A1 Market       B1 <code>      (e.g. 48113, msa:19100, TX)
 *   A2 Survey year  B2 <year|blank> (blank = latest)
 *   A3 <market name, for the reader>
 *   row 5: column names   row 6+: =SHEETYBANKS.MARKET($B$1, A5:J5, $B$2)
 */

export interface MarketSheetSpec {
  baseName: string; // worksheet name; " 2", " 3", … appended if taken
  code: string; // market code in formula syntax
  name: string; // display name, e.g. "Dallas County, TX"
  year?: number; // omitted = latest survey
}

const COLUMNS: { field: string; numberFormat?: string }[] = [
  { field: "RANK" },
  { field: "CERT" },
  { field: "NAME" },
  { field: "HOLDINGCO" },
  { field: "BRANCHES" },
  { field: "DEPOSITS", numberFormat: "#,##0" },
  { field: "SHARE", numberFormat: "0.00" },
  { field: "DEPOSITS_PY", numberFormat: "#,##0" },
  { field: "SHARE_PY", numberFormat: "0.00" },
  { field: "GROWTH", numberFormat: "0.0" },
];

const HEADER_ROW = 5;
const FIRST_DATA_ROW = 6;
// Formats are applied to a generous block below the header: a spilled result
// can't be sized ahead of time, and a market rarely has more institutions.
const FORMAT_ROWS = 600;

export async function insertMarketSheet(spec: MarketSheetSpec): Promise<string> {
  let sheetName = "";
  await Excel.run(async (context) => {
    const worksheets = context.workbook.worksheets;
    worksheets.load("items/name");
    await context.sync();

    const existing = worksheets.items.map((s) => s.name);
    sheetName = spec.baseName;
    for (let i = 2; existing.indexOf(sheetName) !== -1; i++) {
      sheetName = `${spec.baseName} ${i}`;
    }
    const sheet = worksheets.add(sheetName);

    sheet.getRange("A1:B2").values = [
      ["Market", spec.code],
      ["Survey year", spec.year ?? ""],
    ];
    sheet.getRange("A3").values = [
      [`${spec.name} — deposits in $ thousands as of June 30; SHARE and GROWTH in %`],
    ];
    sheet.getRange("A1:A2").format.font.bold = true;
    sheet.getRange("B1:B2").format.fill.color = "#FFF4CE"; // input cells

    const lastCol = colLetter(COLUMNS.length - 1);
    const headerRange = `A${HEADER_ROW}:${lastCol}${HEADER_ROW}`;
    sheet.getRange(headerRange).values = [COLUMNS.map((c) => c.field)];
    sheet.getRange(headerRange).format.font.bold = true;

    sheet.getRange(`A${FIRST_DATA_ROW}`).formulas = [
      [`=SHEETYBANKS.MARKET($B$1, ${headerRange}, $B$2)`],
    ];

    const lastFormatRow = FIRST_DATA_ROW + FORMAT_ROWS - 1;
    COLUMNS.forEach((c, i) => {
      if (!c.numberFormat) return;
      const col = colLetter(i);
      sheet.getRange(`${col}${FIRST_DATA_ROW}:${col}${lastFormatRow}`).numberFormat = Array.from(
        { length: FORMAT_ROWS },
        () => [c.numberFormat as string]
      );
    });

    sheet.getRange(`A1:${lastCol}${HEADER_ROW}`).format.autofitColumns();
    sheet.getRange("C:D").format.columnWidth = 180;
    sheet.activate();
    await context.sync();
  });
  return sheetName;
}
